import type TSquarePaymentRequest from 'bz-core-controls/controls/payments/square/types/square-payment-request';
import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import { ConfigObservable } from 'bz-stores/lib/config';
import { action, computed, observable, runInAction } from 'mobx';
import type { SquareStartPaymentCaptureRequest } from 'tm3-core/api.dtos';

class SquareStore {
  @observable applicationId: string = '';
  client: JsonServiceClient;

  constructor(applicationId: string, isConnect: boolean = false) {
    this.client = new JsonServiceClient();
    const configLength = Object.keys(ConfigObservable.config).length;
    if (!applicationId && configLength !== 0 && !isConnect) {
      throw new Error("SquareStore: No application ID provided, this shouldn't be happening!");
    } else if (!applicationId && configLength === 0 && isConnect) {
      return;
    } else {
      this.applicationId = applicationId;
    }
  }

  @action.bound
  getPaymentFlow(locationId: string): any {
    // find type declarations
    if (!locationId) {
      throw new Error('No ID provided when creating payment flow using location ID');
    }
    return window.Square.payments(this.applicationId, locationId);
  }

  @action.bound
  async initializeCardComponent(
    payments: any,
    mountPointSelector: string,
    styles?: {}
  ): Promise<any> {
    let card;
    if (!!styles) {
      card = await payments.card({
        style: styles
      });
    } else {
      card = await payments.card();
    }
    await card.attach(mountPointSelector);
    return card;
  }

  @action.bound
  async initializeGooglePay(
    payments: any,
    paymentRequest: TSquarePaymentRequest,
    mountPointSelector: string
  ) {
    let googlePay = await payments.googlePay(paymentRequest);
    await googlePay.attach(mountPointSelector);

    return googlePay;
  }

  @action.bound
  async initializeApplePay(payments: any, paymentRequest: TSquarePaymentRequest) {
    let applePay = await payments.applePay(paymentRequest);

    return applePay;
  }

  @action.bound
  buildPaymentRequest(amount: number, paymentFlow: any): TSquarePaymentRequest | undefined {
    if (amount <= 0 || !paymentFlow) {
      return undefined;
    }

    var countryCode = ConfigObservable.config.SQUARE_COUNTRY_CODE;
    var currencyCode = ConfigObservable.config.CURRENCY;

    const result: TSquarePaymentRequest = {
      countryCode: !!countryCode ? countryCode : 'GB',
      currencyCode: !!currencyCode ? currencyCode : 'GBP',

      total: {
        amount: !!amount && amount > 0.0 ? String(amount.toFixed(2)) : '0.00',
        label: 'Total' // this could possibly pick up service name from connect TBD
      }
    };
    return paymentFlow.paymentRequest(result);
  }

  async startPaymentCapture(req: SquareStartPaymentCaptureRequest) {
    return await this.client.post(req);
  }
}

export default SquareStore;
